/**
 * SignaturePad.tsx — a shared, dependency-free draw-on-screen signature pad.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C2.x (clinician UX v2).
 *
 * WHY: clinicians sign prescriptions with a drawn signature (CTO: "give them
 * to sign on the screen") — a file-upload flow assumes they have a scan to
 * hand; most don't. This pad is a plain `<canvas>` driven by POINTER events,
 * so one code path covers mouse, touch and stylus alike; `touch-action: none`
 * stops a finger-drawn stroke scrolling the page instead.
 *
 * Rendering details that matter:
 *   - devicePixelRatio-aware backing store (sized once on mount) — without it
 *     a 2x/3x display renders a blurry, aliased line that looks nothing like
 *     ink. Points are tracked in CSS pixels; the context is scaled once.
 *   - Quadratic MIDPOINT smoothing (curve through each sample toward the next
 *     midpoint) with round lineJoin/lineCap — raw lineTo segments produce a
 *     jagged polyline at pointer-event sample rates.
 *   - Ink is the theme's text token (resolved from the canvas's computed
 *     style, with a dark-green fallback where custom properties don't resolve,
 *     e.g. jsdom); the background stays transparent so the PNG composites
 *     cleanly onto the white prescription PDF.
 *
 * Strokes live in a ref as a stack (array of point arrays) so Undo is a pop +
 * full redraw; a stroke COUNT in state drives the enabled/disabled controls
 * without re-rendering on every pointermove. Export is
 * `canvas.toBlob('image/png')` — the signed upload URL pins image/png.
 *
 * A11y: the pad is a labelled group with a visible note that drawing works
 * with mouse, finger or stylus; Clear/Undo/Save are real buttons. There is no
 * keyboard path to DRAW a signature (inherent to the medium) — the note keeps
 * the expectation honest rather than pretending otherwise.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from './kit';

interface Point {
  x: number;
  y: number;
}

/** Fallback ink where CSS custom properties don't resolve (tests/jsdom). */
const INK_FALLBACK = '#1f3329';

/** CSS-pixel canvas height; width follows the container. */
const PAD_HEIGHT = 180;

/** Fallback CSS width where layout yields no measure (tests/jsdom). */
const WIDTH_FALLBACK = 480;

export function SignaturePad({
  saving = false,
  saveLabel = 'Save signature',
  onSave,
}: {
  /** Disables every control while the save round-trip is in flight. */
  saving?: boolean;
  saveLabel?: string;
  /** Receives the drawn signature as a PNG blob. */
  onSave: (blob: Blob) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokesRef = useRef<Point[][]>([]);
  const drawingRef = useRef(false);
  const inkRef = useRef(INK_FALLBACK);
  // Only the COUNT is state — it drives the buttons; points never re-render.
  const [strokeCount, setStrokeCount] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Size the backing store once, devicePixelRatio-aware, then scale the
    // context so all drawing happens in CSS pixels.
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth || WIDTH_FALLBACK;
    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(PAD_HEIGHT * dpr);
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);
    inkRef.current = getComputedStyle(canvas).getPropertyValue('--gps-text').trim() || INK_FALLBACK;
  }, []);

  /** Full clear + redraw of every stroke (also how Undo/Clear take effect). */
  function redraw() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    ctx.strokeStyle = inkRef.current;
    ctx.fillStyle = inkRef.current;
    ctx.lineWidth = 2.5;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    for (const points of strokesRef.current) {
      if (points.length === 0) continue;
      if (points.length === 1) {
        // A tap/click leaves a dot, not nothing.
        ctx.beginPath();
        ctx.arc(points[0].x, points[0].y, 1.25, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      // Midpoint smoothing: curve through each sample toward the next midpoint.
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length - 1; i += 1) {
        const midX = (points[i].x + points[i + 1].x) / 2;
        const midY = (points[i].y + points[i + 1].y) / 2;
        ctx.quadraticCurveTo(points[i].x, points[i].y, midX, midY);
      }
      const last = points[points.length - 1];
      ctx.lineTo(last.x, last.y);
      ctx.stroke();
    }
  }

  function pointFrom(event: React.PointerEvent<HTMLCanvasElement>): Point {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function handlePointerDown(event: React.PointerEvent<HTMLCanvasElement>) {
    if (saving) return;
    drawingRef.current = true;
    // Keep the stroke even when the pointer leaves the canvas mid-draw
    // (guarded — jsdom has no pointer capture).
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Capture is best-effort; drawing works without it.
      }
    }
    strokesRef.current.push([pointFrom(event)]);
    setStrokeCount(strokesRef.current.length);
    redraw();
  }

  function handlePointerMove(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    strokesRef.current[strokesRef.current.length - 1]?.push(pointFrom(event));
    redraw();
  }

  function handlePointerEnd() {
    drawingRef.current = false;
  }

  function handleClear() {
    strokesRef.current = [];
    setStrokeCount(0);
    redraw();
  }

  function handleUndo() {
    strokesRef.current.pop();
    setStrokeCount(strokesRef.current.length);
    redraw();
  }

  function handleSave() {
    const canvas = canvasRef.current;
    // The signed URL pins image/png — export is ALWAYS a PNG.
    canvas?.toBlob?.((blob) => {
      if (blob) onSave(blob);
    }, 'image/png');
  }

  const empty = strokeCount === 0;

  return (
    <div role="group" aria-label="Draw your signature">
      <p style={{ color: 'var(--gps-muted)', fontSize: '13px', margin: '0 0 var(--gps-s-2)' }}>
        Draw your signature below with your mouse, finger or stylus.
      </p>
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        aria-label="Signature drawing area"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        style={{
          display: 'block',
          width: '100%',
          height: `${PAD_HEIGHT}px`,
          boxSizing: 'border-box',
          border: '1.5px dashed var(--gps-border-strong)',
          borderRadius: 'var(--gps-r-md)',
          background: 'var(--gps-surface)',
          touchAction: 'none',
          cursor: 'crosshair',
        }}
      />
      <div style={{ display: 'flex', gap: 'var(--gps-s-2)', marginTop: 'var(--gps-s-3)', flexWrap: 'wrap' }}>
        <Button type="button" variant="ghost" onClick={handleClear} disabled={empty || saving} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14px' }}>
          Clear
        </Button>
        <Button type="button" variant="ghost" onClick={handleUndo} disabled={empty || saving} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14px' }}>
          Undo
        </Button>
        <Button type="button" variant="primary" onClick={handleSave} disabled={empty || saving} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14px' }}>
          {saving ? 'Saving…' : saveLabel}
        </Button>
      </div>
    </div>
  );
}
